import { type ModalOptions, modalController } from '@ionic/vue'

import ModalVerification from '~/views/profile/verification/ModalVerification.vue'
import ModalOtpCodeVerification from '~/views/common/ModalOtpCodeVerification.vue'
import { useAuthStore, useRestaurantStore, useUserStore } from '~/store'
import type { ViewSource } from '~/types/route'
import type { UserResponse } from '~/types/api/data-contracts'

const verificationModalId = 'verification-modal'
const emailVerificationModalId = 'email-verification-modal'
const mobileVerificationModalId = 'mobile-verification-modal'
const twoFactorVerificationModalId = '2fa-verification-modal'
const weeksToTriggerVerificationPrompt = 2
const enableShowPromptOnHomeLanding = false

interface VerificationModalProps {
  source: ViewSource
  title: string
  message: string
  showPasswordInput?: boolean
  transparentModalStyle?: boolean
  onSubmit: (...args: any[]) => Promise<void>
  onSuccessfulVerifyCode: (...args: any[]) => Promise<void>
  onSignOut: () => void
  onDismiss: () => void
}

/**
 * @description Handler to display a modal for the user to verify their account.
 */
export function useVerificationModal() {
  const modalOptions: ModalOptions = {
    breakpoints: [0, 1],
    component: ModalVerification,
    id: verificationModalId
  }

  /**
   * @description Display the verification modal
   */
  async function showModal(source: ViewSource, wasAutoShown: boolean): Promise<void> {
    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        ...{ modalId: verificationModalId, source, wasAutoShown }
      }
    })

    if (!modal) {
      return
    }

    gaEventVerificationViewed(source)
    return await modal.present()
  }

  async function getIsVerificationModalOpen(): Promise<boolean> {
    const topModal = await modalController.getTop()
    return topModal?.id === verificationModalId
  }

  /**
   * @description Record that an order has been made, if the user is not verified
   */
  function recordOrderHasBeenMade(): void {
    const { isAuthenticated, authUser } = useAuthStore()
    const userStore = useUserStore()
    if (isAuthenticated && !isFullyVerified(authUser)) {
      // Increment the order count if the user is eligible to rate
      userStore.ordersCompletedSincePromptedToVerify++
    }
  }

  function isFullyVerified(authUser: UserResponse | undefined): boolean | undefined {
    return authUser?.isEmailVerified && authUser?.isMobileVerified
  }

  async function performVerificationDisplayCheck(source: ViewSource, currentPath: string | undefined): Promise<void> {
    if (currentPath === '/checkout') {
      return
    }

    const { isAuthenticated, authUser } = useAuthStore()
    if (!isAuthenticated) {
      return
    }

    // Check if the user is verified
    if (isFullyVerified(authUser)) {
      return
    }

    const userStore = useUserStore()

    // Check if the user has made any orders since the last time they were prompted to verify, or if the prompt is enabled on home landing
    if (!(enableShowPromptOnHomeLanding || userStore.ordersCompletedSincePromptedToVerify > 0)) {
      return
    }

    const { dateLastShownVerifyModal } = useUserStore()
    if (dateLastShownVerifyModal) {
      const lastShownDate = new Date(dateLastShownVerifyModal)
      const now = new Date()
      const weeksSinceLastShown = (now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24 * 7)
      if (weeksSinceLastShown <= weeksToTriggerVerificationPrompt) {
        return
      }
    }

    // Check if there are any prominent promotions or order config confirmation showing
    const { isModalOpen: isProminentPromotionModalOpen } = useProminentPromotions()
    const { isOrderConfigConfirmationModalOpen } = useOrderConfigConfirmationModal()
    const { getWillShowOrderConfigConfirmation } = useRestaurantStore()

    if (
      isProminentPromotionModalOpen.value ||
      isOrderConfigConfirmationModalOpen.value ||
      getWillShowOrderConfigConfirmation
    ) {
      return
    }

    // If not, track that the modal was automatically shown and the verification modal

    userStore.dateLastShownVerifyModal = new Date().toISOString()
    const wasAutoShown = true

    await showModal(source, wasAutoShown)
  }

  return {
    showVerificationModal: showModal,
    verificationModalId,
    performVerificationDisplayCheck,
    recordOrderHasBeenMade,
    getIsVerificationModalOpen
  }
}

export function useEmailVerificationModal() {
  const modalOptions: ModalOptions = {
    breakpoints: [0, 1],
    component: ModalOtpCodeVerification,
    id: emailVerificationModalId
  }

  /**
   * @description Display the email verification modal
   */
  async function showModal(props: VerificationModalProps) {
    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        ...{ modalId: emailVerificationModalId },
        ...props
      },
      cssClass: props.transparentModalStyle ? 'transparent-modal' : undefined
    })

    if (!modal) {
      return
    }

    return await modal.present()
  }

  return {
    showEmailVerificationModal: showModal,
    emailVerificationModalId
  }
}

export function useMobileVerificationModal() {
  const modalOptions: ModalOptions = {
    breakpoints: [0, 1],
    component: ModalOtpCodeVerification,
    id: mobileVerificationModalId
  }

  /**
   * @description Display the mobile verification modal
   */
  async function showModal(props: VerificationModalProps) {
    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        ...{ modalId: mobileVerificationModalId },
        ...props
      },
      cssClass: props.transparentModalStyle ? 'transparent-modal' : undefined
    })

    if (!modal) {
      return
    }

    return await modal.present()
  }

  return {
    showMobileVerificationModal: showModal,
    mobileVerificationModalId
  }
}

export function useTwoFactorAuthenticationModal() {
  const modalOptions: ModalOptions = {
    breakpoints: [0, 1],
    component: ModalOtpCodeVerification,
    id: twoFactorVerificationModalId
  }

  /**
   * @description Display the mobile verification modal
   */
  async function showModal(props: VerificationModalProps) {
    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        ...{ modalId: twoFactorVerificationModalId },
        ...props
      },
      cssClass: props.transparentModalStyle ? 'transparent-modal' : undefined
    })

    if (!modal) {
      return
    }

    return await modal.present()
  }

  return {
    showTwoFactorVerificationModal: showModal,
    twoFactorVerificationModalId
  }
}
